import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-br from-slate-50 via-rose-50/30 to-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="font-semibold text-sm uppercase tracking-wider" style={{ color: '#6b3d52' }}>
            Get In Touch
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mt-3 mb-6">
            Let's Work Together
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Have a project in mind or just want to chat about design? 
            I'd love to hear from you. Drop me a message!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6">
                Contact Information
              </h3>
              <p className="text-slate-600 mb-8">
                Feel free to reach out through any of these channels. 
                I typically respond within 24 hours.
              </p>
            </div>

            {/* Contact Cards */}
            <div className="space-y-4">
              {/* Email */}
              <div className="flex items-center gap-4 p-6 bg-white rounded-xl border border-slate-200 hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: '#fdf2f8' }}>
                  <i className="ri-mail-line text-xl" style={{ color: '#6b3d52' }}></i>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Email</h4>
                  <a
                    href="mailto:charitrpatel@gmail.com"
                    className="text-slate-600 transition-colors duration-300"
                    onMouseEnter={(e) => e.currentTarget.style.color = '#6b3d52'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#475569'}
                  >
                    charitrpatel@gmail.com
                  </a>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-center gap-4 p-6 bg-white rounded-xl border border-slate-200 hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: '#fdf2f8' }}>
                  <i className="ri-phone-line text-xl" style={{ color: '#6b3d52' }}></i>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Phone</h4>
                  <a
                    href="tel:+19055989502"
                    className="text-slate-600 transition-colors duration-300"
                    onMouseEnter={(e) => e.currentTarget.style.color = '#6b3d52'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#475569'}
                  >
                    +1 905 598 9502
                  </a>
                </div>
              </div>

              {/* Location */}
              <div className="flex items-center gap-4 p-6 bg-white rounded-xl border border-slate-200 hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: '#fdf2f8' }}>
                  <i className="ri-map-pin-line text-xl" style={{ color: '#6b3d52' }}></i>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Location</h4>
                  <p className="text-slate-600">Toronto, CA</p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div>
              <h4 className="font-semibold text-slate-900 mb-4">Follow Me</h4>
              <div className="flex gap-3">
                <a
                  href="https://www.linkedin.com/in/charit-patel-2533581ba/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 flex items-center justify-center rounded-xl hover:scale-110 transition-all duration-300 cursor-pointer"
                  style={{ backgroundColor: '#6b3d52' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7d4a60'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6b3d52'}
                >
                  <i className="ri-linkedin-fill text-xl text-white"></i>
                </a>
                <a
                  href="https://www.behance.net/charitpatel06"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 flex items-center justify-center rounded-xl hover:scale-110 transition-all duration-300 cursor-pointer"
                  style={{ backgroundColor: '#6b3d52' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7d4a60'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6b3d52'}
                >
                  <i className="ri-behance-fill text-xl text-white"></i>
                </a>
                <a
                  href="https://www.instagram.com/ui_charit/?hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 flex items-center justify-center rounded-xl hover:scale-110 transition-all duration-300 cursor-pointer"
                  style={{ backgroundColor: '#6b3d52' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7d4a60'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6b3d52'}
                >
                  <i className="ri-instagram-line text-xl text-white"></i>
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-10">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              Send Me a Message
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 transition-all duration-300"
                  style={{ focusRingColor: '#6b3d52' }}
                  onFocus={(e) => e.currentTarget.style.borderColor = '#6b3d52'}
                  onBlur={(e) => e.currentTarget.style.borderColor = '#cbd5e1'}
                  placeholder="John Doe"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                  Your Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 transition-all duration-300"
                  onFocus={(e) => e.currentTarget.style.borderColor = '#6b3d52'}
                  onBlur={(e) => e.currentTarget.style.borderColor = '#cbd5e1'}
                  placeholder="john@example.com"
                />
              </div>

              {/* Subject */}
              <div>
                <label htmlFor="subject" className="block text-sm font-semibold text-slate-700 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 transition-all duration-300"
                  onFocus={(e) => e.currentTarget.style.borderColor = '#6b3d52'}
                  onBlur={(e) => e.currentTarget.style.borderColor = '#cbd5e1'}
                  placeholder="Project Inquiry"
                />
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 transition-all duration-300 resize-none"
                  onFocus={(e) => e.currentTarget.style.borderColor = '#6b3d52'}
                  onBlur={(e) => e.currentTarget.style.borderColor = '#cbd5e1'}
                  placeholder="Tell me about your project..."
                ></textarea>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full px-8 py-4 text-white font-semibold rounded-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{ backgroundColor: '#6b3d52' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7d4a60'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6b3d52'}
              >
                Send Message
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
